import classes from './FormAlert.module.scss'
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo } from 'react-icons/fi'
import { HiOutlineLightBulb } from 'react-icons/hi'
import { useEffect, useId, useRef, useState } from 'react'
import type { IconType } from 'react-icons/lib'
import type { ReactNode } from 'react'

type AlertType = 'info' | 'success' | 'warning' | 'error' | 'tip' | 'none'

export interface ExpandableProps {
  isExpand: boolean
  expandButton: ReactNode
}

export interface NonExpandableProps {
  isExpand?: never
  expandButton?: never
}

export interface BaseProps {
  children: ReactNode
  title?: string
  type?: AlertType
}

type Props = BaseProps & (ExpandableProps | NonExpandableProps)

const icons: Record<AlertType, IconType> = {
  info: FiInfo,
  success: FiCheckCircle,
  warning: FiAlertTriangle,
  error: FiAlertCircle,
  tip: HiOutlineLightBulb,
  none: FiInfo
}

const FormAlert = ({
  children,
  title = '',
  type = 'none',
  isExpand = false,
  expandButton
}: Props) => {
  const [isAtMaxHeight, setIsAtMaxHeight] = useState <boolean>(true)
  const contentContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    if (contentContainer) {
      setIsAtMaxHeight(contentContainer.scrollHeight > contentContainer.clientHeight)
    }
  }, [contentContainerRef])

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    if (!isExpand && contentContainer && expandButton) {
      contentContainer.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [contentContainerRef, expandButton, isExpand])

  // There can be multiple errors on the page
  // So each needs a unique ID to prevent conflict
  const id = useId()

  const labelId = id + 'label'
  const descriptionId = id + 'description'

  const Icon = icons[type]

  const expandedClass = isExpand
    ? classes.isExpanded
    : ''

  const getClasses = (type: AlertType) => [
    classes.Root,
    expandedClass,
    classes[type]
  ].filter(classStyle => classStyle).join(' ')

  const getContentClasses = () => [
    classes.content,
    expandedClass
  ].filter(classStyle => classStyle).join(' ')

  return (
    <div
      aria-describedby={descriptionId}
      aria-labelledby={labelId}
      className={getClasses(type)}
      role='alert'
    >
      {
        type !== 'none' &&
          <Icon
            aria-hidden
            className={classes.icon}
            size='16px'
          />
      }
      <div>
        <div
          className={getContentClasses()}
          ref={contentContainerRef}
        >
          {
            title &&
              <span
                className={classes.title}
                id={labelId}
              >
                {title}
              </span>
          }
          <div id={descriptionId}>
            {children}
          </div>
        </div>
        {
          isAtMaxHeight && expandButton &&
            <div className={classes.button}>
              {expandButton}
            </div>
        }
      </div>
    </div>
  )
}

export default FormAlert
